import { useCallback, useEffect, useState } from 'react';
import {
  CalendarDays,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Megaphone,
  Plus,
} from 'lucide-react';
import { announcementsApi } from '../api/announcements';
import type { Announcement } from '../api/announcements';
import type { Me } from '../api/auth';
import { eventsApi } from '../api/events';
import type { EventAgendaPage, EventCalendarPage, EventSummary } from '../api/events';
import { whatsNewApi } from '../api/whatsNew';
import type { SeenBatch } from '../api/whatsNew';
import { useMarkSeen } from '../hooks/useMarkSeen';
import type { MarkShown } from '../hooks/useMarkSeen';
import { AnnouncementCard } from '../ui/AnnouncementCard';
import { useKeepAttachmentsSigned } from '../ui/AnnouncementAttachments';
import { eventColorVar } from '../brand/eventColors';
import { formatDayInZone, formatTimeInZone, isForeignZone } from '../lib/datetime';
import { keepNewForTheVisit } from '../lib/whatsNew';
import { monthLabel, neighbours } from '../lib/eventMonths';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { EventAgenda } from '../ui/EventAgenda';
import { EventSearch } from '../ui/EventSearch';
import { NewMarker } from '../ui/NewMarker';
import { UnreadDot } from '../ui/UnreadDot';
import { EventMonthSection, MonthBar } from '../ui/MonthBar';

/**
 * Announcement cards drawn before the rest go behind *Show N more*.
 *
 * Three, because a noticeboard is what a unit is saying now and the calendar is what people came
 * for. This page drew all 268 of the dev troop's, above the calendar, at 54,360px (TROOP-96).
 */
const NOTICES_DRAWN = 3;

/**
 * The month an event belongs to, `yyyy-MM`, in the **event's** zone.
 *
 * Not the reader's: an event at 9pm on the 31st belongs to the month its unit is in, and a parent
 * travelling must see it filed where the troop filed it.
 */
/*
 * `monthOf` used to live here, re-deriving each event's month from `startsAt` and `timeZoneId`
 * with its own `Intl.DateTimeFormat`. It is gone: the server sends `month` on every row now
 * (TROOP-123), computed by `EventMonthWindow.MonthOf`, which is also what builds the month index
 * the navigation steps through. Two implementations of "which month is this in" agree everywhere
 * except at boundaries -- the one place the answer matters, and the last place anybody looks.
 */

/**
 * The unit landing page: what is coming up, and what has been announced.
 *
 * The product's claim is that the answer to "what time do we leave" is a field on a record, so the
 * record is what a member lands on. Administration is one tap away and no longer on this page at
 * all — TROOP-120 moved the roster, the subgroups, the kinds of event and the packing lists onto
 * `ManageScreen`, and dropped the Conversations button that duplicated the header's icon. What is
 * left in the header row are the two things a leader does *from a calendar*: add an event, or tell
 * everybody something.
 */
export function UnitHomeScreen({
  unitId,
  unitName,
  canCreateEvents,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  canCreateEvents: boolean;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [agenda, setAgenda] = useState<EventAgendaPage | null>(null);
  /**
   * Which view is on screen — TROOP-213. The agenda is what the page opens on; the calendar is one
   * tap away and unchanged.
   *
   * Not a URL segment: it is a view of the same page rather than a place, and a member who lands
   * here from the header icon should get the landing view every time rather than whatever they
   * last looked at.
   */
  const [view, setView] = useState<'agenda' | 'calendar'>('agenda');
  const [calendar, setCalendar] = useState<EventCalendarPage | null>(null);
  const [movingMonth, setMovingMonth] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[] | null>(null);
  const [announcementTotal, setAnnouncementTotal] = useState(0);
  const [showAllNotices, setShowAllNotices] = useState(false);
  /** The header's count, read beside the cards it counts -- see `AppHeader`'s `newCount`. */
  const [newCount, setNewCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // What this page draws is what this member has been shown (TROOP-268): the noticeboard's cards
  // and the agenda's rows, as each one mounts. Never the calendar's or a search's.
  const markShown = useMarkSeen(
    useCallback((batch: SeenBatch) => whatsNewApi.markSeen(unitId, batch), [unitId]),
  );

  const load = useCallback(async () => {
    // The unit switcher hands this screen a new unitId in place; the last unit's count must not
    // sit on the icon while this one's is read.
    setNewCount(null);

    try {
      // Both, together. An announcement that failed to load while the events succeeded would
      // leave the page looking complete and missing the thing that sits above everything.
      //
      // The calendar is deliberately NOT fetched here: it is a whole month of a unit that may
      // carry hundreds, and nobody has asked for it yet. It loads on the first tap of View all.
      //
      // The count rides the same read so it is taken before anything on this page is marked seen:
      // fetched beside the marks, it could land after them and read 0 above cards saying New.
      const [loadedAgenda, loadedAnnouncements, loadedNew] = await Promise.all([
        eventsApi.agenda(unitId),
        announcementsApi.list(unitId),
        whatsNewApi.count(unitId),
      ]);
      setAgenda(loadedAgenda);
      setNewCount(loadedNew.total);
      setAnnouncements(loadedAnnouncements.rows);
      setAnnouncementTotal(loadedAnnouncements.total);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this unit.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The announcements alone, re-read before their documents' signed URLs lapse (TROOP-255). Not
  // `load`, which would re-fetch the agenda every few minutes to refresh a link.
  const refreshAnnouncements = useCallback(() => {
    announcementsApi
      .list(unitId)
      .then((page) => {
        // New kept for the visit: this page has marked these seen by now, and the re-read is for
        // the links, not for taking the marker off a card the reader may not have reached.
        setAnnouncements((previous) => keepNewForTheVisit(previous, page.rows));
        setAnnouncementTotal(page.total);
      })
      .catch(() => {
        // The links on screen stay as they were; the next tap or reload says what is wrong.
      });
  }, [unitId]);

  useKeepAttachmentsSigned(announcements, refreshAnnouncements);

  /**
   * Walking to another month is a fresh read, because the window is the server's to decide -- a
   * unit has thousands of events and the month index is built over all of them, so neither the
   * slice nor the counts can be worked out here.
   */
  async function moveTo(month?: string) {
    setMovingMonth(true);
    try {
      setCalendar(await eventsApi.calendar(unitId, month));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that month.');
    } finally {
      setMovingMonth(false);
    }
  }

  /** View all. The calendar is fetched on the first tap and kept after that. */
  async function showCalendar() {
    setView('calendar');
    if (!calendar) await moveTo();
  }

  // Months in the order the window returned them, each with its events. The rows arrive already
  // ordered by month and then by start time, so nothing here re-sorts -- the server decided the
  // window and the order, and re-deciding either would be a second answer.
  const months: { month: string; label: string; events: EventSummary[] }[] = [];

  for (const event of calendar?.events ?? []) {
    const last = months[months.length - 1];

    if (last && last.month === event.month) {
      last.events.push(event);
    } else {
      months.push({ month: event.month, label: monthLabel(event.month), events: [event] });
    }
  }

  const { previous, next } = neighbours(calendar?.months ?? [], months);

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} newCount={newCount} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-4)' }}
            >
              {/* Was "What's coming up" until TROOP-123, and the rename is not cosmetic: the
                  window is whole months now and opens on the current one, so the page shows the
                  campout three weeks ago as well as the one on Saturday. A heading promising only
                  the future above a list that reaches backwards is a heading that lies, and this
                  is what the header's own icon has always called this page anyway. */}
              Calendar
            </h1>

            <div className="flex flex-wrap gap-3">
              {canCreateEvents ? (
                <Button variant="primary" onClick={() => onNavigate(`/units/${unitId}/events/new`)}>
                  <Plus size={16} aria-hidden="true" />
                  Add an event
                </Button>
              ) : (
                // Not a disabled button. A member is not waiting on a permission that might
                // arrive; scheduling is what officers do, and saying so beats a grey control.
                <span
                  className="t-caption"
                  style={{ color: 'var(--color-text-secondary)', alignSelf: 'center' }}
                >
                  Your unit&rsquo;s officers add events to this calendar.
                </span>
              )}

              {canCreateEvents && (
                <Button
                  variant="secondary"
                  onClick={() => onNavigate(`/units/${unitId}/announcements/new`)}
                >
                  <Megaphone size={16} aria-hidden="true" />
                  Post an announcement
                </Button>
              )}

              {/* Five buttons came off this row in TROOP-120, and only one of them was ever an
                  action: Conversations duplicated the header icon, and Roster, the subgroups,
                  Kinds of event and What to bring are administration that now lives on Manage --
                  which is where the *Kinds of event* button's own comment had said it belonged
                  since the day it was written. What is left is what a leader actually does from a
                  calendar: put something on it, or tell everybody something. */}
            </div>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {/* Above the calendar, because that is what standing information is: it is true until
                somebody says otherwise, where an event is a thing on a date. The design system's
                rule that these stick to the top of the feed, applied to the feed. Absent entirely
                when there are none -- an empty state here would be a box explaining a thing the
                unit has simply not done yet, above the thing they came for. */}
            {announcements && announcements.length > 0 && (
              <ul
                className="flex flex-col"
                style={{
                  gap: 'var(--space-3)',
                  listStyle: 'none',
                  margin: '0 0 var(--space-5)',
                  padding: 0,
                }}
              >
                {(showAllNotices ? announcements : announcements.slice(0, NOTICES_DRAWN)).map(
                  (announcement) => (
                    <li key={announcement.id}>
                      <AnnouncementCard
                        announcement={announcement}
                        onOpenThread={(conversationId) =>
                          onNavigate(`/units/${unitId}/conversations/${conversationId}`)
                        }
                        onShown={markShown}
                      />
                    </li>
                  ),
                )}

                {/* What was bounded, said out loud. A noticeboard that quietly stopped at three
                    would read as a unit that has said three things. The count is the unit's real
                    total, not the number the server sent -- the server bounds too (TROOP-96). */}
                {!showAllNotices && announcementTotal > NOTICES_DRAWN && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setShowAllNotices(true)}
                      className="t-caption text-left"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        padding: 'var(--space-2) var(--space-1)',
                        minHeight: 'var(--tap-min)',
                        textDecoration: 'underline',
                      }}
                    >
                      {announcements.length < announcementTotal
                        ? `Show ${announcements.length - NOTICES_DRAWN} more of ${announcementTotal} announcements`
                        : `Show ${announcementTotal - NOTICES_DRAWN} more`}
                    </button>
                  </li>
                )}
              </ul>
            )}

            {/* The landing view -- TROOP-213. The calendar below is right and was the wrong thing
                to open on: it takes months whole, so on the 4th the top of the page was the 1st.
                One component with the family screen, because rule 13b says the two doors mirror
                each other in routes and never in rules. */}
            {view === 'agenda' && agenda && (
              <EventAgenda
                agenda={agenda}
                onViewAll={() => void showCalendar()}
                // Offered only to somebody who can actually do it (spec §6).
                emptyAction={
                  canCreateEvents
                    ? {
                        label: 'Add an event',
                        onClick: () => onNavigate(`/units/${unitId}/events/new`),
                      }
                    : undefined
                }
                renderItem={(event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    onOpen={() => onNavigate(`/units/${unitId}/events/${event.id}`)}
                    // The agenda alone marks: drawing a row here is being shown it.
                    onShown={markShown}
                  />
                )}
              />
            )}

            {view === 'calendar' && (
              <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
                {/* Reachable back again, which is half of what makes the agenda a landing view
                    rather than a redirect. */}
                <div>
                  <Button variant="secondary" onClick={() => setView('agenda')}>
                    <ChevronLeft size={16} aria-hidden="true" />
                    Back to what is coming up
                  </Button>
                </div>

                {/* Search lives here and not on the landing view -- TROOP-214. This is the
                    all-events view, which is where somebody looking for "when did we last run the
                    klondike" already is, and keeping it off the agenda is the point of the split.
                    Not in the header either: TROOP-120 cut that bar to three icons after it ran
                    off the side of a 390px phone, and a search icon is the likeliest fourth.

                    Only once the unit has something to search. A box over a calendar with nothing
                    on it invites a question with one possible answer. */}
                {(calendar?.months.length ?? 0) > 0 && (
                  <EventSearch
                    // Keyed on the unit, because this route does not remount when the switcher
                    // changes units -- it hands the same element a new prop. Without the key, a
                    // leader who searched in the Troop and switched to the Pack would be looking
                    // at the Troop's rows under the Pack's page, and tapping one would navigate to
                    // /units/{pack}/events/{troopEvent}. The key is what makes the search a fact
                    // about the unit on screen rather than about the component's lifetime.
                    key={unitId}
                    search={(query) => eventsApi.search(unitId, query)}
                    renderItem={(event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        onOpen={() => onNavigate(`/units/${unitId}/events/${event.id}`)}
                      />
                    )}
                  />
                )}

                {calendar?.events.length === 0 && (
                  <EmptyState
                    icon={CalendarDays}
                    headline="Nothing on the calendar"
                    explanation="Add the next campout or meeting and every family has the same answer, in the same place."
                    action={
                      canCreateEvents
                        ? {
                            label: 'Add an event',
                            onClick: () => onNavigate(`/units/${unitId}/events/new`),
                          }
                        : undefined
                    }
                  />
                )}

                {months.length > 0 && (
                  <>
                    {/* The same control the conversations page's events section uses, because it
                        is literally the same component -- somebody learns this once. Both steps
                        move over months that actually hold an event, so neither can land on an
                        empty page. */}
                    <MonthBar
                      index={calendar?.months ?? []}
                      shown={months}
                      previous={previous}
                      next={next}
                      busy={movingMonth}
                      onAnchorChange={(month) => void moveTo(month)}
                    />

                    {/* A heading on every month, unlike the old conditional one: the calendar
                        reaches backwards, so "which month am I looking at" is a live question even
                        when the window holds one. The month is the EVENT's, so a parent reading
                        from another zone sees it filed where their unit filed it. What is drawn is
                        bounded and the rest is one tap away -- see EventMonthSection. */}
                    {months.map((group) => (
                      <EventMonthSection
                        key={group.month}
                        label={group.label}
                        items={group.events}
                        renderItem={(event) => (
                          <EventRow
                            key={event.id}
                            event={event}
                            onOpen={() => onNavigate(`/units/${unitId}/events/${event.id}`)}
                          />
                        )}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function EventRow({
  event,
  onOpen,
  onShown,
}: {
  event: EventSummary;
  onOpen: () => void;
  /** The agenda's rows only. The calendar and search draw New and leave it standing. */
  onShown?: MarkShown;
}) {
  // The departure is the fact people come back for, so it is the one on the row. Falling back to
  // the start time rather than hiding the line: a meeting has no separate departure.
  const when = event.departsAt ?? event.startsAt;
  const zone = event.timeZoneId;
  const cancelled = event.status === 'Cancelled';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center justify-between gap-4 text-left"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        border: 'none',
        // The type's colour, as a 4px left rule. One of the four surfaces it is allowed on, and
        // the one that makes a wall of identical rows scannable -- is this the campout or the
        // Monday meeting is a question a family should answer by seeing rather than by reading.
        // Never a fill, never a background: this is a rule and a label, nothing larger.
        borderLeft: `4px solid ${eventColorVar(event.eventTypeColorKey)}`,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        padding: 'var(--space-4)',
        minHeight: 'var(--tap-min)',
      }}
    >
      <span className="min-w-0">
        <span
          className="t-body-strong inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {/* Before the title rather than at the card's top-right: these rows are a list, not the
              §5B card, and the spec's placement there assumes a date block anchoring the corner. */}
          {/* New to this member (TROOP-268), and a different fact from the dot beside it, which is
              unread messages in the event's thread. The word for one and the dot for the other, so
              neither mark means two things. */}
          {event.isNew && <NewMarker kind="event" id={event.id} onShown={onShown} />}
          {event.unreadCount > 0 && <UnreadDot count={event.unreadCount} />}
          {/* Struck through, not hidden and not removed from the list. A family who already
              packed has to see that the campout was called off; a row that quietly vanished
              reads as something they misremembered. */}
          <span style={cancelled ? { textDecoration: 'line-through' } : undefined}>
            {event.title}
          </span>
        </span>

        {/* What this one is about, when somebody has said (TROOP-158). Absent, never a
            placeholder -- most events on a calendar have not been planned yet, and a row
            with nothing under it is the right rendering of that. */}
        {event.theme && (
          <span
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', display: 'block' }}
          >
            {event.theme}
          </span>
        )}

        {cancelled && (
          <span
            className="t-caption inline-flex items-start gap-1"
            style={{ color: 'var(--color-status-blocked)', display: 'flex' }}
          >
            <CalendarX2 size={13} aria-hidden="true" style={{ marginTop: '0.15em', flexShrink: 0 }} />
            {/* The reason on the row itself. "Cancelled" alone makes a parent open the page to
                learn whether it is the rain or the drivers, which is the extra hop this product
                exists to remove. */}
            <span>Cancelled{event.cancellationReason ? ` — ${event.cancellationReason}` : ''}</span>
          </span>
        )}

        <span
          className="t-caption tabular"
          style={{ color: 'var(--color-text-secondary)', display: 'block' }}
        >
          {/* The kind first, where a scanning eye lands before the date. "Campout" and "Troop
              meeting" are what a parent is actually sorting the list by -- and the name always
              renders as text beside the colour, because colour is never the only signal. */}
          {event.eventTypeName && (
            <>
              <span style={{ color: eventColorVar(event.eventTypeColorKey), fontWeight: 600 }}>
                {event.eventTypeName}
              </span>{' '}
              &middot;{' '}
            </>
          )}
          {formatDayInZone(when, zone)} &middot; {formatTimeInZone(when, zone)}
          {event.departsAt && ' depart'}
          {/* Only when the viewer is somewhere else. In the unit's own zone this would be noise
              on every row; outside it, its absence is a wrong time. */}
          {isForeignZone(zone) && ` (${zone.replace(/_/g, ' ')})`}
        </span>

        {event.locationName && (
          <span
            className="t-caption inline-flex items-center gap-1"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <MapPin size={13} aria-hidden="true" />
            {event.locationName}
          </span>
        )}
      </span>

      <ChevronRight
        size={18}
        aria-hidden="true"
        style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}
      />
    </button>
  );
}
